import { TranslateService } from '@ngx-translate/core';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, firstValueFrom, map } from 'rxjs';
import { AddIframeComponent } from 'src/app/dialogs/add-iframe/add-iframe.component';
import { MatDialog } from '@angular/material/dialog';
import { UiService } from 'src/app/services/ui.service';
import { FeatureManagementService } from 'src/app/services/feature-management.service';
import { FLAGS, FlagsService } from 'src/app/services/flags.service';
import { SpacePermissionsManagerService } from 'src/app/services/space-permissions-manager.service';
import { SpacePermissions } from 'src/app/models/session';
import { SpaceRepository } from 'src/app/state/space.repository';
import { NotificationToasterService } from '../../../../services/notification-toaster.service';
import {
  CollaborativeApps,
  IAppInformation,
  IAppModalInformation,
} from '../../iframe/additional-apps.utils';
import { TelemetryService } from '../../../../services/telemetry.service';
import {
  SessionSharedDataService,
  SessionView,
} from '../../../../services/session-shared-data.service';
import { modifiedSetTimeout } from '../../../../utilities/ZoneUtils';
import { ItemModel } from '../../../../models/itemModel';
import { BoardOperationsService } from '../../services/board-operations.service';
import { HyperbeamService } from '../../../../services/hyperbeam.service';
import { INFOS } from '../../../../common/utils/notification-constants';
import { detectYoutube } from '../../../../utilities/video.utils';
import { MarioService } from '../../../../mario.service';
import { BoardItemsManager } from '../board-items/board-items-manager';

export interface WebViewerConfig {
  webgl?: boolean;
  defaultUrl?: string;
  adblock?: boolean;
  country?: string;
  title?: string;
  initialZoom?: number;
  appInstanceId?: string;
  isPrivate?: boolean;
  addToNewBoard?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AppsInsertionService {
  private readonly APP_CREATION_TIMEOUT_DURATION = 5 * 1000; // 5 seconds.

  public _appDisabled$ = new BehaviorSubject<string | null>(null);
  public appDisabled$ = this._appDisabled$.asObservable();

  constructor(
    private telemetry: TelemetryService,
    private sharedDataService: SessionSharedDataService,
    private boardOperationsService: BoardOperationsService,
    private hyperbeamService: HyperbeamService,
    private dialog: MatDialog,
    private uiService: UiService,
    private featureManagementService: FeatureManagementService,
    private flagsService: FlagsService,
    private spacePermissionsManagerService: SpacePermissionsManagerService,
    private sessionSharedDataService: SessionSharedDataService,
    private spaceRepo: SpaceRepository,
    private notificationToasterService: NotificationToasterService,
    private translate: TranslateService,
    private marioService: MarioService,
  ) {
    this.appDisabled$.subscribe((disabledApp) => {
      if (disabledApp) {
        modifiedSetTimeout(() => {
          // the app is still disabled after 5 seconds
          if (this._appDisabled$.getValue() === disabledApp) {
            // enable insertion again
            this._appDisabled$.next(null);
          }
        }, this.APP_CREATION_TIMEOUT_DURATION);
      }
    });
  }

  readonly canInsertWebViewer$ = combineLatest([
    this.featureManagementService.isWebViewerEnabled$.pipe(
      map(
        (webViewerEnabled) =>
          webViewerEnabled &&
          this.flagsService.isFlagEnabled(FLAGS.MARIO) &&
          (this.flagsService.featureFlagsVariables.mario.enable_browser as boolean),
      ),
    ),
    this.sessionSharedDataService.canModifySession$,
    this.spacePermissionsManagerService.isPermissionEnabled$(SpacePermissions.INSERT_WEB_VIEWER),
    this.spaceRepo.isCurrentUserHost$,
  ]).pipe(
    map(
      ([isWebViewerEnabled, canModifySession, permissionEnabled, isCurrentUserHost]) =>
        isWebViewerEnabled && canModifySession && (isCurrentUserHost || permissionEnabled),
    ),
    distinctUntilChanged(),
  );

  private beforeWebViewerAddition(appId: string) {
    this._appDisabled$.next(appId);

    this.telemetry.event('new_app_opened', { appType: appId });

    this.sharedDataService.sessionView.switchToSessionView(SessionView.WHITEBOARD);
  }

  private beforeMarioOrIframeAddition(appId: string) {
    this.telemetry.event('new_app_opened', { appType: appId });

    this.sharedDataService.sessionView.switchToSessionView(SessionView.WHITEBOARD);
  }

  public beforeAppInsertion(itemModel: ItemModel, appId: string, insertInNewBoard = false) {
    if (insertInNewBoard) {
      this.boardOperationsService.insertNewFrameAndSelectIt();
    }
    if (itemModel === ItemModel.WebViewer) {
      this.beforeWebViewerAddition(appId);
    } else if (itemModel === ItemModel.Mario || ItemModel.IFrame) {
      this.beforeMarioOrIframeAddition(appId);
    }
  }

  public enableWebViewerInsertion() {
    this._appDisabled$.next(null);
  }

  /**
   * Creates a hyperbeam session and inserts a web viewer board item
   * @param config Configuration for the web viewer
   * @returns Promise<boolean> indicating success
   */
  public async createAndInsertWebViewer(config: WebViewerConfig = {}): Promise<boolean> {
    const appType = config.appInstanceId ?? CollaborativeApps.WEBVIEWER;

    this.beforeAppInsertion(ItemModel.WebViewer, appType, config?.addToNewBoard ?? false);

    try {
      const hbSession = await this.hyperbeamService.createHbSession(undefined, {
        webgl: config.webgl ?? false,
        defaultUrl: config.defaultUrl,
        adblock: config.adblock ?? true,
        country: config.country,
      });

      const sessionId = hbSession.data?.session_id;
      const embedUrl = hbSession.data?.embed_url;

      if (!sessionId || !embedUrl) {
        this.enableWebViewerInsertion();
        return false;
      }

      BoardItemsManager.instance?.insertNewBoardItem<ItemModel.WebViewer>(
        ItemModel.WebViewer,
        {
          hyperbeamSessionId: sessionId,
          embedUrl,
          isPortalSession: hbSession.data?.is_portal_session,
          remoteControlToken: hbSession.data?.remote_control_token,
          webGLEnabled: hbSession.data?.is_portal_session ? true : config.webgl,
          title: config.title,
          initialZoom: config.initialZoom,
          country: config.country,
          adBlockEnabled: config.adblock,
          startUrl: config.defaultUrl,
          isPrivate: config.isPrivate,
          appInstanceId: config.appInstanceId,
        },
        {
          renderInNearestFreeSpace: true,
        },
      );

      return true;
    } catch {
      this.enableWebViewerInsertion();

      this.notificationToasterService.showDefaultErrorNotificationV2(
        this.translate.instant('Failed to launch app'),
        this.translate.instant(
          "We're experiencing high demand, so some apps are temporarily unavailable. Please wait a few minutes and try again.",
        ),
        this.notificationToasterService.generateNotificationCode(),
      );

      return false;
    }
  }

  public async insertMario(
    app: IAppInformation,
    isPrivate: boolean = false,
    addToNewBoard: boolean = false,
  ) {
    let resource = app?.marioAppConfig?.mario_resource;
    if (app.uid === CollaborativeApps.MARIO) {
      const url = await this.openIframeUrlDialogAndGetValue(app.modalConfig!);
      if (url) {
        resource = { _type: 'DEV_URL', url };
      } else {
        return;
      }
    }

    this.beforeAppInsertion(ItemModel.Mario, app.uid!, addToNewBoard);
    BoardItemsManager.instance?.insertNewBoardItem<ItemModel.Mario>(
      ItemModel.Mario,
      {
        appInstanceId: app.uid,
        title: app.title,
        resource,
        isPrivate,
      },
      {
        renderInNearestFreeSpace: true,
      },
    );
  }

  async insertIframe(
    app: IAppInformation,
    isPrivate: boolean,
    addToNewBoard: boolean,
  ): Promise<void> {
    const url =
      app?.iframeAppConfig?.defaultUrl ??
      (await this.openIframeUrlDialogAndGetValue(app.modalConfig!));

    if (!url) {
      return;
    }

    this.beforeAppInsertion(ItemModel.IFrame, app.uid!, addToNewBoard);
    BoardItemsManager.instance?.insertNewBoardItem<ItemModel.IFrame>(
      ItemModel.IFrame,
      {
        appInstanceId: app.uid,
        url,
        isPrivate,
      },
      {
        renderInNearestFreeSpace: true,
      },
    );
  }

  public async openIframeUrlDialogAndGetValue(appModalConfig: IAppModalInformation) {
    const width = this.uiService.isMobile ? 'auto' : '90vw';
    return (
      await firstValueFrom(
        this.dialog
          .open(AddIframeComponent, {
            disableClose: false,
            data: { modalConfig: appModalConfig },
            panelClass: 'add-iframe-dialog',
            width: width,
            minWidth: width,
            maxWidth: width,
          })
          .afterClosed(),
      )
    ).url;
  }

  /**
   * Inserts the enabled YouTube app for the given video url, either as a web viewer
   * or as the Mario app, depending on which one is enabled.
   */
  public async insertYoutubeApp(youtubeApp: IAppInformation, url: string): Promise<void> {
    if (youtubeApp.uid !== CollaborativeApps.AI_YOUTUBE) {
      this.insertYoutubeMarioApp(youtubeApp, url);
      return;
    }

    const notificationCode = INFOS.INSERTING_YOUTUBE_APP;
    // Because the insertion of web viewer takes sometime, so we show this indication.
    // Mario on the other hand is instance
    this.notificationToasterService.showLoadingNotification(
      this.translate.instant('Preparing your YouTube video...'),
      notificationCode,
      true,
      false,
      undefined,
      true,
    );
    try {
      await this.createAndInsertWebViewer({
        defaultUrl: url,
        appInstanceId: CollaborativeApps.AI_YOUTUBE,
        adblock: youtubeApp.webviewerAppConfig?.adblock,
        title: youtubeApp.title,
      });
    } finally {
      this.notificationToasterService.dismissLoadingNotification(notificationCode);
    }
  }

  // Mirrors the videoId/playerState state contract the deployed 'ia-yt' Mario app listens for
  // (see MarioService.setInitialState usage for the Timer/Stopwatch Mario apps for the same pattern).
  private insertYoutubeMarioApp(youtubeApp: IAppInformation, url: string): void {
    const resource = youtubeApp.marioAppConfig?.mario_resource;
    if (!resource) {
      return;
    }

    const item = BoardItemsManager.instance?.insertNewBoardItem<ItemModel.Mario>(
      ItemModel.Mario,
      {
        resource,
        appInstanceId: youtubeApp.uid,
        title: youtubeApp.title,
      },
      {
        renderInNearestFreeSpace: true,
      },
    );

    const videoId = detectYoutube(new URL(url));
    if (item && videoId) {
      this.marioService.setInitialState(item.configurations.contentId, {
        videoId: `youtube/${videoId}`,
        playerState: 'not_started',
        playbackRate: 1,
        playbackOffset: 0,
      });
    }
  }

  public async insertWebViewer(
    app: IAppInformation,
    isPrivate: boolean = false,
    addToNewBoard: boolean = false,
  ): Promise<void> {
    const appWebViewerConfig = app.webviewerAppConfig;

    await this.createAndInsertWebViewer({
      webgl: appWebViewerConfig?.webgl,
      defaultUrl: appWebViewerConfig?.defaultUrl,
      adblock: appWebViewerConfig?.adblock,
      country: appWebViewerConfig?.country,
      title: app.title,
      initialZoom: appWebViewerConfig?.initialZoom,
      appInstanceId: app.uid,
      isPrivate,
      addToNewBoard,
    });
  }
}
